<script setup lang="ts">
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { Building2, MapPin } from 'lucide-vue-next'
import { ref, watch } from 'vue'

import AddressForm, { type AddressFields } from '@/components/AddressForm.vue'
import InputError from '@/components/InputError.vue'
import NotificationSettings from '@/components/settings/NotificationSettings.vue'
import TokenList from '@/components/settings/TokenList.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PortalLayout from '@/layouts/PortalLayout.vue'
import {
  portalOrganizationNotificationPreferencePath,
  portalOrganizationSettingsPath,
  portalOrganizationTokenPath,
  portalOrganizationTokensPath,
  portalOrganizationWebhookSubscriptionPath,
  portalOrganizationWebhookSubscriptionsPath,
  testPortalOrganizationWebhookSubscriptionPath,
} from '@/routes'

interface Organization {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
}

interface SubscribableEvent {
  key: string
  label: string
}

interface WebhookDelivery {
  id: number
  status: string
  event: string | null
  response_status: number | null
  delivered_at: string | null
  created_at: string
  payload: Record<string, unknown> | string | null
}

interface WebhookSubscription {
  id: number
  url: string
  events: string
  active: boolean
  created_at: string
  last_delivery: WebhookDelivery | null
  recent_deliveries: WebhookDelivery[]
}

interface NotificationPreference {
  email_enabled: boolean
  email_events: string[]
}

interface Token {
  id: number
  name: string
  access: 'read' | 'read_write'
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

const props = defineProps<{
  organization: Organization
  address: AddressFields | null
  notification_preference: NotificationPreference
  subscribable_events: SubscribableEvent[]
  webhook_subscriptions: WebhookSubscription[]
  tokens: Token[]
}>()

// --- Tab state (driven by ?tab= query param, set by the controllers on redirect) ---
const TABS = ['profile', 'notification', 'api-token'] as const
type SettingsTab = (typeof TABS)[number]

function tabFromUrl(): SettingsTab {
  const url = new URL(usePage().url, window.location.origin)
  const tab = url.searchParams.get('tab') as SettingsTab | null
  return tab && TABS.includes(tab) ? tab : 'profile'
}

const activeTab = ref<SettingsTab>(tabFromUrl())

watch(
  () => usePage().url,
  () => {
    activeTab.value = tabFromUrl()
  },
)

function switchTab(tab: string | number) {
  activeTab.value = tab as SettingsTab
  history.replaceState(null, '', `?tab=${tab}`)
}

const form = useForm({
  name: props.organization.name,
  contact: props.organization.contact,
  email: props.organization.email,
  phone: props.organization.phone,
  address_attributes: {
    unit_no: props.address?.unit_no ?? '',
    street_no: props.address?.street_no ?? '',
    street_name: props.address?.street_name ?? '',
    street_type: props.address?.street_type ?? '',
    city: props.address?.city ?? '',
    state: props.address?.state ?? '',
    postcode: props.address?.postcode ?? '',
  },
})

function save() {
  form.patch(portalOrganizationSettingsPath())
}
</script>

<template>
  <Head title="Settings" />
  <PortalLayout actor-type="organization">
    <div class="space-y-6">
      <h1 class="text-xl font-semibold">Settings</h1>

      <Tabs v-model="activeTab" @update:model-value="switchTab">
        <TabsList class="h-11 w-full">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notification">Notification</TabsTrigger>
          <TabsTrigger value="api-token">API Token</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <!-- Organization Profile -->
          <Card>
            <CardHeader>
              <div class="flex items-center gap-2">
                <Building2 class="text-muted-foreground size-4" />
                <CardTitle class="text-base">Organization Profile</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form class="grid gap-4 sm:grid-cols-2" @submit.prevent="save">
                <div class="grid gap-2">
                  <Label for="name">Name</Label>
                  <Input id="name" v-model="form.name" required />
                  <InputError :messages="form.errors.name" />
                </div>
                <div class="grid gap-2">
                  <Label for="contact">Contact Person</Label>
                  <Input id="contact" v-model="form.contact" required />
                  <InputError :messages="form.errors.contact" />
                </div>
                <div class="grid gap-2">
                  <Label for="email">Email</Label>
                  <Input id="email" v-model="form.email" type="email" required />
                  <InputError :messages="form.errors.email" />
                </div>
                <div class="grid gap-2">
                  <Label for="phone">Phone</Label>
                  <Input id="phone" v-model="form.phone" type="tel" required />
                  <InputError :messages="form.errors.phone" />
                </div>
                <div class="space-y-4 border-t pt-4 sm:col-span-2">
                  <div class="flex items-center gap-2">
                    <MapPin class="text-muted-foreground size-4" />
                    <p class="text-sm font-medium">Address</p>
                  </div>
                  <AddressForm v-model="form.address_attributes" :errors="form.errors" error-prefix="address." />
                </div>

                <div class="flex justify-end sm:col-span-2">
                  <Button type="submit" :disabled="form.processing">Save Changes</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notification" class="space-y-8">
          <NotificationSettings
            :recipient-email="organization.email"
            :notification-preference="notification_preference"
            :subscribable-events="subscribable_events"
            :webhook-subscriptions="webhook_subscriptions"
            :preference-path="portalOrganizationNotificationPreferencePath()"
            :webhook-create-path="portalOrganizationWebhookSubscriptionsPath()"
            :webhook-path="(id) => portalOrganizationWebhookSubscriptionPath(id)"
            :webhook-test-path="(id) => testPortalOrganizationWebhookSubscriptionPath(id)"
          />
        </TabsContent>

        <TabsContent value="api-token">
          <TokenList
            :tokens="tokens"
            :create-path="portalOrganizationTokensPath()"
            :destroy-path="(id) => portalOrganizationTokenPath(id)"
          />
        </TabsContent>
      </Tabs>
    </div>
  </PortalLayout>
</template>
